import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Store,
  Tag,
  Settings2,
  Users2,
  Wallet,
  CreditCard,
  ShieldAlert,
  Compass,
  BarChart3,
  Sparkles,
  LogOut,
  X,
  Menu,
  CheckCheck,
  Repeat,
  Plug,
  Gift,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/merchants", label: "Merchants", icon: Store },
  { to: "/admin/offers", label: "Offers", icon: Tag },
  { to: "/admin/commission", label: "Commission Rules", icon: Settings2 },
  { to: "/admin/users", label: "Users", icon: Users2 },
  { to: "/admin/cashback", label: "Cashback Approvals", icon: CheckCheck },
  { to: "/admin/payouts", label: "Payouts", icon: Wallet },
  { to: "/admin/fraud", label: "Fraud / Compliance", icon: ShieldAlert },
  { to: "/admin/affiliate-hunter", label: "Affiliate Hunter AI", icon: Compass },
  { to: "/admin/affiliate-networks", label: "Affiliate Networks", icon: Plug },
  { to: "/admin/campaigns", label: "Bonus Campaigns", icon: Gift },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
];

export default function AdminSidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const initials = (user?.name || user?.email || "A").trim().slice(0, 1).toUpperCase();

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-72 bg-gray-900 text-gray-100 transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-gray-800">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-amber-600 text-white flex items-center justify-center">
              <Repeat className="w-5 h-5" />
            </div>
            <span className="font-display font-bold tracking-tight">Admin · LC</span>
          </Link>
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-800"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-3 space-y-0.5 overflow-y-auto h-[calc(100vh-9rem)]">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              data-testid={`admin-sidebar-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-emerald-700 text-white font-semibold" : "text-gray-300 hover:bg-gray-800"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-800 bg-gray-900">
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-amber-600 text-white font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-gray-400 capitalize">{user?.role?.replace("_", " ")}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-sidebar-logout"
              className="p-2 rounded-lg hover:bg-gray-800 text-gray-300 hover:text-red-400"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export function AdminTopbar({ onMenu }) {
  return (
    <div className="lg:hidden sticky top-0 z-30 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
      <button
        onClick={onMenu}
        className="p-2 rounded-lg hover:bg-gray-100"
        data-testid="admin-topbar-menu"
      >
        <Menu className="w-5 h-5" />
      </button>
      <span className="font-display font-bold">Admin · LC</span>
      <div className="w-9" />
    </div>
  );
}
